import { ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'audio[controls]',
  'video[controls]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableIn(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
}

interface DialogProps {
  /** Names the dialog for assistive tech. Always required, even when visually hidden. */
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Render the title bar visually. Set false when the design already shows a heading inside. */
  showTitle?: boolean;
  /**
   * id of a heading already present in `children` to name the dialog with. Use this instead of
   * showTitle={false} when the content has its own visible heading — otherwise the dialog's
   * hidden heading and the visible one say the same thing twice.
   */
  labelledBy?: string;
  /** Suppress Escape and backdrop dismissal — e.g. while a delete is already in flight. */
  busy?: boolean;
  maxWidth?: string;
  /** Padding of the panel; pass '0' for media-style dialogs that fill their own frame. */
  padding?: string;
  /** Transparent panel for lightboxes, where the image is the whole content. */
  bare?: boolean;
}

/**
 * A modal dialog that a screen reader user can actually use.
 *
 * Every overlay in this app used to be a bare `position: fixed` div. For assistive tech that is
 * not a dialog at all: pressing the button appeared to do nothing, focus stayed behind on the
 * trigger, Tab wandered off into the page underneath, and Escape did not close anything. This
 * component supplies the five things that were missing everywhere:
 *
 *   1. role="dialog" + aria-modal, named by its title
 *   2. focus moved inside on open, and restored to the trigger on close
 *   3. Tab cycles within the dialog instead of escaping into the background
 *   4. Escape closes
 *   5. the rest of the app is made `inert`, so it disappears from the reading order
 *
 * It renders through a portal into <body> precisely so that #root can be marked inert without
 * the dialog marking itself inert too.
 */
export default function Dialog({
  title,
  onClose,
  children,
  showTitle = true,
  labelledBy,
  busy = false,
  maxWidth = '420px',
  padding = '1.5rem',
  bare = false,
}: DialogProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Keep the latest onClose/busy without re-running the mount effect, which would otherwise
  // re-steal focus on every parent render.
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  onCloseRef.current = onClose;
  busyRef.current = busy;

  useEffect(() => {
    const root = document.getElementById('root');
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;

    if (root) {
      root.setAttribute('inert', '');
      // Safari shipped `inert` late; aria-hidden covers the gap for screen readers.
      root.setAttribute('aria-hidden', 'true');
    }
    document.body.style.overflow = 'hidden';

    // Focus the first control, falling back to the panel itself so the dialog's own name is
    // what gets announced rather than nothing.
    const first = focusableIn(panelRef.current)[0];
    (first ?? panelRef.current)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busyRef.current) {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = focusableIn(panelRef.current);
      if (items.length === 0) {
        e.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === firstItem || active === panelRef.current)) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && active === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (root) {
        root.removeAttribute('inert');
        root.removeAttribute('aria-hidden');
      }
      document.body.style.overflow = prevOverflow;
      // Returning focus is what stops the screen reader from dumping the user at the top of the
      // document every time a dialog closes.
      previouslyFocused?.focus?.();
    };
  }, []);

  return createPortal(
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        overflowY: 'auto',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy ?? titleId}
        tabIndex={-1}
        style={{
          background: bare ? 'transparent' : '#1a1730',
          border: bare ? 'none' : '1px solid rgba(102,126,234,0.35)',
          borderRadius: bare ? 0 : '16px',
          padding: bare ? 0 : padding,
          maxWidth,
          width: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          boxShadow: bare ? 'none' : '0 20px 60px rgba(0,0,0,0.5)',
          outline: 'none',
        }}
      >
        {!labelledBy && (
        <div
          style={
            showTitle
              ? { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.75rem' }
              : undefined
          }
        >
          <h2
            id={titleId}
            className={showTitle ? undefined : 'sr-only'}
            style={showTitle ? { margin: 0, color: '#f7fafc', fontSize: '1.15rem', lineHeight: 1.3 } : undefined}
          >
            {title}
          </h2>
          {showTitle && (
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              aria-label={t('a11y.closeDialog')}
              style={{
                flexShrink: 0,
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.08)',
                color: '#e2e8f0',
                fontSize: '1rem',
                cursor: busy ? 'not-allowed' : 'pointer',
                opacity: busy ? 0.5 : 1,
              }}
            >
              <span aria-hidden="true">✕</span>
            </button>
          )}
        </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}
